// @flow
import React from 'react';
import {useLocation, Link} from 'react-router-dom';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import {HomeOutlined, ReceiptOutlined, Add} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import {
  AppBar,
  Toolbar,
  Typography,
  Hidden,
  ButtonBase,
} from '@material-ui/core';

import type {Store, StoreTier} from '../../../entities';

type Props = {
  store?: Store,
  storePreviousTier?: StoreTier,
};

const useStyles = makeStyles(theme => ({
  appBar: {
    bottom: 0,
    height: 64,
    left: 0,
    boxShadow: '0px -4px 12px rgba(213, 213, 213, 0.15)',
    backgroundColor: 'white',
    justifyContent: 'center',
    top: 'auto',
    zIndex: 1300,
  },
  bottomNavbarWrap: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    zIndex: 1300,
    height: 64,
    display: 'flex',
  },

  navbarLink: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dot: {
    width: 5,
    height: 5,
    backgroundColor: 'transparent',
    borderRadius: '100%',
    marginTop: 10,
  },
  active: {
    backgroundColor: 'black',
  },
  toolsIcon: {
    backgroundColor: colors.grey900,
    borderRadius: '50%',
    color: 'white !important',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0',
    boxShadow: '0px -4px 12px rgba(213, 213, 213, 0.15)',

    [theme.breakpoints.up('sm')]: {
      padding: '0 16px',
    },
    [theme.breakpoints.up('lg')]: {
      justifyContent: 'space-around',
    },
  },
  fabButton: {
    position: 'relative',
    zIndex: 1,
    top: -18,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: colors.primary,
    gap: '0.5rem',
    '&:hover': {
      backgroundColor: 'transparent',
    },

    '&:hover $fabIcon': {
      backgroundColor: 'rgba(152, 82, 231, .85)',
    },
  },
  fabIcon: {
    boxShadow: '0px 4px 24px rgba(152, 82, 231, .85)',
    backgroundColor: colors.primary,
    fontSize: 24,
    width: 48,
    height: 48,
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      width: 56,
      height: 56,
    },
  },
  navbarItem: {
    color: colors.grey600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    flexGrow: 1,
    '&[data-active="true"]': {
      color: colors.primary,
    },
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '0 16px',
    },
  },
  navbarItemLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'inherit',
  },
  fabButtonLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'inherit',
    textAlign: 'center',
    maxWidth: '10ch',

    [theme.breakpoints.up('sm')]: {
      maxWidth: 'initial',
    },
  },
}));

function BottomNavbarItem(props: {
  icon: React$Node,
  href: string,
  active: boolean,
  label: string,
}) {
  const {icon, href, active, label} = props;
  const classes = useStyles();

  return (
    <ButtonBase
      aria-label={label}
      to={href}
      component={Link}
      data-active={active}
      className={classes.navbarItem}
    >
      <span>{icon}</span>
      <Typography variant="body2" className={classes.navbarItemLabel}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

function BottomNavbarFab(props: Props) {
  const {icon, href, active, label} = props;
  const classes = useStyles();

  return (
    <ButtonBase
      aria-label={label}
      to={href}
      component={Link}
      data-active={active}
      disableRipple
      className={classes.fabButton}
    >
      <span className={classes.fabIcon}>{icon}</span>
      <Typography variant="body2" className={classes.fabButtonLabel}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

export default function BottomNavbar(props: Props) {
  const classes = useStyles();
  const location = useLocation();
  const {pathname} = location;

  if (
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/on-boarding')
  )
    return null;

  return (
    <Hidden mdUp>
      <AppBar position="sticky" className={classes.appBar}>
        <Toolbar variant="dense" className={classes.toolbar}>
          <BottomNavbarItem
            icon={<HomeOutlined />}
            href="/home"
            active={pathname === '/home'}
            label="Inicio"
          />
          <BottomNavbarItem
            icon={<ReceiptOutlined />}
            href="/orders"
            active={pathname === '/orders'}
            label="Pedidos"
          />

          <BottomNavbarFab
            icon={<Add />}
            href="/orders/manual-order"
            active={pathname === '/orders/manual-order'}
            label="Crear pedido"
          />
          <BottomNavbarItem
            icon={<LocalMallOutlinedIcon />}
            href="/products"
            active={pathname === '/products'}
            label="Productos"
          />
          <BottomNavbarItem
            icon={<MoreHorizIcon />}
            href="/tools"
            active={pathname === '/tools'}
            label="Otros"
          />
        </Toolbar>
      </AppBar>
    </Hidden>
  );
}
